import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  House, Lightning, BookOpen, Cross, Trophy, ChartLineUp,
  Users, Crown, Briefcase, Coins, Brain, Compass,
  Gear, SignOut, Sparkle, Play, Link as LinkIcon,
  Wallet, Globe, ArrowRight, Moon, Sun
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const MenuCard = ({ to, icon: Icon, title, subtitle, color, badge, locked, delay = 0, isDark = true }) => {
  const content = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className={`group relative p-5 border transition-all duration-300 cursor-pointer overflow-hidden ${locked ? 'opacity-60' : ''} ${
        isDark ? 'bg-[#121212] border-[#262626] hover:border-[#C4A661]/40' : 'bg-white border-[#E5E0D5] hover:border-[#A8893E]/40 shadow-sm'
      }`}
      data-testid={`menu-${to.replace(/\//g, '-').slice(1)}`}
    >
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: `linear-gradient(135deg, ${color}08, transparent)` }} />
      <div className="relative flex items-start gap-4">
        <div className="w-12 h-12 flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
          <Icon size={24} style={{ color }} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`text-sm font-bold group-hover:text-[#C4A661] transition-colors ${isDark ? 'text-[#F4F0E6]' : 'text-[#1A1A1A]'}`}>{title}</h3>
            {badge && <span className="text-[8px] px-1.5 py-0.5 font-bold uppercase tracking-wider" style={{ color: badge.color, backgroundColor: `${badge.color}15` }}>{badge.text}</span>}
            {locked && <Crown size={12} className="text-[#C4A661]" weight="fill" />}
          </div>
          <p className={`text-xs mt-1 leading-relaxed ${isDark ? 'text-[#666]' : 'text-[#888]'}`}>{subtitle}</p>
        </div>
        <ArrowRight size={16} className={`group-hover:text-[#C4A661] transition-colors shrink-0 mt-1 ${isDark ? 'text-[#333]' : 'text-[#CCC]'}`} />
      </div>
    </motion.div>
  );

  return <Link to={to}>{content}</Link>;
};

export default function MainMenuPage() {
  const { user, logout, isPaidMember } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const hasInnerCircle = isPaidMember || isAdmin;
  const isDark = theme === 'dark';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#050505] text-[#F4F0E6]' : 'bg-[#F5F2EB] text-[#1A1A1A]'}`} data-testid="main-menu-page">
      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur-md border-b transition-colors duration-300 ${isDark ? 'bg-[#0A0A0A]/95 border-[#1A1A1A]' : 'bg-white/95 border-[#E5E0D5]'}`}>
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/hub" className="text-2xl font-extrabold tracking-tighter text-[#C4A661]" data-testid="logo-link">POWR</Link>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Link to="/admin-dashboard" className={`text-xs uppercase tracking-widest transition-colors hidden sm:block ${isDark ? 'text-[#A3A3A3] hover:text-[#F4F0E6]' : 'text-[#888] hover:text-[#1A1A1A]'}`}>Admin</Link>
            )}
            <button onClick={toggleTheme} className={`w-8 h-8 flex items-center justify-center border transition-all ${isDark ? 'border-[#333] text-[#A3A3A3] hover:text-[#C4A661] hover:border-[#C4A661]' : 'border-[#DDD] text-[#888] hover:text-[#A8893E] hover:border-[#A8893E]'}`} data-testid="theme-toggle">
              {isDark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <Link to="/profile" className="w-8 h-8 bg-[#C4A661] flex items-center justify-center font-bold text-[#0A0A0A] text-sm hover:ring-2 hover:ring-[#C4A661] transition-all">
              {user?.name?.charAt(0) || '?'}
            </Link>
            <button onClick={handleLogout} className="text-[#666] hover:text-[#EF4444] transition-colors" data-testid="logout-btn">
              <SignOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Welcome */}
      <div className="max-w-6xl mx-auto px-4 pt-10 pb-6">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-sm text-[#C4A661] uppercase tracking-widest mb-1">Welcome back</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">
            {user?.name || 'King'}
          </h1>
          <p className={`text-sm mt-2 ${isDark ? 'text-[#666]' : 'text-[#999]'}`}>Purpose. Ownership. Wealth. Responsibility.</p>
        </motion.div>
      </div>

      {/* Menu Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-16 space-y-8">

        {/* Primary Section */}
        <div>
          <h2 className={`text-xs font-bold uppercase tracking-widest mb-4 px-1 ${isDark ? 'text-[#666]' : 'text-[#999]'}`}>Main</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <MenuCard isDark={isDark} to="/community" icon={House} title="Community" subtitle="Connect with the brotherhood. Feed, posts, and discussions." color="#C4A661" delay={0.05} />
            <MenuCard isDark={isDark} to="/freedom-path" icon={Lightning} title="Freedom Path" subtitle="AI coaching, debt elimination, challenges, and financial tools." color="#10B981" badge={{ text: 'New', color: '#10B981' }} delay={0.1} />
            <MenuCard isDark={isDark} to="/devotionals" icon={Compass} title="Devotionals" subtitle="Daily faith-based devotionals and spiritual growth." color="#8B5CF6" delay={0.15} />
          </div>
        </div>

        {/* Learning & Growth */}
        <div>
          <h2 className={`text-xs font-bold uppercase tracking-widest mb-4 px-1 ${isDark ? 'text-[#666]' : 'text-[#999]'}`}>Learning & Growth</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <MenuCard isDark={isDark} to="/courses" icon={BookOpen} title="Training Courses" subtitle="POWR curriculum, AI Income Blueprint, and more." color="#3B82F6" delay={0.2} />
            <MenuCard isDark={isDark} to="/videos" icon={Play} title="Video Library" subtitle="Curated video content for growth and education." color="#EF4444" delay={0.25} />
            <MenuCard isDark={isDark} to="/getting-started" icon={Sparkle} title="Getting Started" subtitle="Step-by-step onboarding guide for new members." color="#F59E0B" delay={0.3} />
          </div>
        </div>

        {/* Investment Tools */}
        <div>
          <h2 className={`text-xs font-bold uppercase tracking-widest mb-4 px-1 ${isDark ? 'text-[#666]' : 'text-[#999]'}`}>Investment Tools</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <MenuCard isDark={isDark} to="/5-movement-platform" icon={Coins} title="$5 Movement" subtitle="Start investing with just $5. Build the habit." color="#10B981" delay={0.35} />
            <MenuCard isDark={isDark} to="/paper-portfolio-platform" icon={Briefcase} title="Paper Portfolio" subtitle="Practice investing without risk. Learn by doing." color="#C4A661" delay={0.4} />
            <MenuCard isDark={isDark} to="/wealth-intelligence" icon={Brain} title="Wealth Intelligence" subtitle="AI-powered market insights and education." color="#8B5CF6" delay={0.45} />
          </div>
        </div>

        {/* Inner Circle */}
        <div>
          <div className="flex items-center gap-2 mb-4 px-1">
            <Crown size={14} className="text-[#C4A661]" weight="fill" />
            <h2 className="text-xs font-bold text-[#C4A661] uppercase tracking-widest">Inner Circle</h2>
            {!hasInnerCircle && <span className="text-[10px] px-2 py-0.5 bg-[#C4A661]/20 text-[#C4A661]">Premium</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <MenuCard isDark={isDark} to={hasInnerCircle ? "/inner-circle-hub" : "/inner-circle-sales"} icon={Crown} title="IC Dashboard" subtitle="Premium training, mastermind access, and resources." color="#C4A661" locked={!hasInnerCircle} delay={0.5} />
            <MenuCard isDark={isDark} to={hasInnerCircle ? "/business-suite" : "/inner-circle-sales"} icon={Briefcase} title="Business Suite" subtitle="Full business toolkit for Inner Circle members." color="#C4A661" locked={!hasInnerCircle} delay={0.55} />
            {!hasInnerCircle && (
              <MenuCard isDark={isDark} to="/inner-circle-sales" icon={Sparkle} title="Join Inner Circle" subtitle="Unlock premium features, courses, and 1-on-1 coaching." color="#C4A661" badge={{ text: 'Upgrade', color: '#C4A661' }} delay={0.6} />
            )}
          </div>
        </div>

        {/* Resources */}
        <div>
          <h2 className={`text-xs font-bold uppercase tracking-widest mb-4 px-1 ${isDark ? 'text-[#666]' : 'text-[#999]'}`}>Resources</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <MenuCard isDark={isDark} to="/resources" icon={BookOpen} title="Resource Hub" subtitle="Tools, guides, and downloadable resources." color="#F59E0B" delay={0.65} />
            <MenuCard isDark={isDark} to="/links" icon={LinkIcon} title="Links & Resources" subtitle="Curated external links and partner resources." color="#A3A3A3" delay={0.7} />
            <MenuCard isDark={isDark} to="/profile" icon={Gear} title="Profile & Settings" subtitle="Manage your account, preferences, and privacy." color="#666" delay={0.75} />
          </div>
        </div>

        {/* Admin */}
        {isAdmin && (
          <div>
            <div className="flex items-center gap-2 mb-4 px-1">
              <Globe size={14} className="text-[#8B5CF6]" />
              <h2 className="text-xs font-bold text-[#8B5CF6] uppercase tracking-widest">Admin Tools</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <MenuCard isDark={isDark} to="/admin-dashboard" icon={Globe} title="Admin Dashboard" subtitle="Manage users, content, and platform settings." color="#8B5CF6" delay={0.8} />
              <MenuCard isDark={isDark} to="/website-generator" icon={Globe} title="Website Generator" subtitle="Build and manage custom landing pages." color="#8B5CF6" delay={0.85} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
